/**
 * Inline SVG icon set — outline style, 1.5px stroke, 24×24 viewBox.
 *
 * Replaces the emoji icons used previously by the navigation, tiles
 * and taskbar.  Each icon is a stand-alone React component to keep
 * tree-shaking clean and avoid pulling a runtime icon library.
 */
import type { SVGProps } from "react";

export type IconName =
  | "home"
  | "dashboard"
  | "droplet"
  | "flask"
  | "report"
  | "wrench"
  | "users"
  | "settings"
  | "shield"
  | "logout"
  | "search"
  | "bell"
  | "plus"
  | "menu"
  | "chevron-right"
  | "wifi"
  | "server"
  | "clock";

interface IconProps extends SVGProps<SVGSVGElement> {
  name: IconName;
  size?: number;
}

function Base({
  size = 18,
  strokeWidth = 1.6,
  children,
  ...rest
}: SVGProps<SVGSVGElement> & { size?: number; children?: React.ReactNode }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export function Icon({ name, size, ...rest }: IconProps): JSX.Element {
  switch (name) {
    case "home":
      return (
        <Base size={size} {...rest}>
          <path d="M3 11.5 12 4l9 7.5" />
          <path d="M5 10v9a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1v-9" />
        </Base>
      );
    case "dashboard":
      return (
        <Base size={size} {...rest}>
          <rect x="3" y="3" width="7" height="9" rx="1.5" />
          <rect x="14" y="3" width="7" height="5" rx="1.5" />
          <rect x="14" y="12" width="7" height="9" rx="1.5" />
          <rect x="3" y="16" width="7" height="5" rx="1.5" />
        </Base>
      );
    case "droplet":
      return (
        <Base size={size} {...rest}>
          <path d="M12 3.5c2.4 3 6 6.6 6 10.4a6 6 0 1 1-12 0c0-3.8 3.6-7.4 6-10.4Z" />
          <path d="M9 14a3 3 0 0 0 3 3" />
        </Base>
      );
    case "flask":
      return (
        <Base size={size} {...rest}>
          <path d="M9 3h6" />
          <path d="M10 3v6L4.5 18.5A2 2 0 0 0 6.2 21h11.6a2 2 0 0 0 1.7-2.5L14 9V3" />
          <path d="M7.5 14h9" />
        </Base>
      );
    case "report":
      return (
        <Base size={size} {...rest}>
          <path d="M7 3h7l5 5v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" />
          <path d="M14 3v5h5" />
          <path d="M9 13h6M9 17h4" />
        </Base>
      );
    case "wrench":
      return (
        <Base size={size} {...rest}>
          <path d="M14.7 3.3a5 5 0 0 0-6.4 6.4L3 15v3h3l5.3-5.3a5 5 0 0 0 6.4-6.4l-3 3-2.4-.6-.6-2.4 3-3Z" />
        </Base>
      );
    case "users":
      return (
        <Base size={size} {...rest}>
          <circle cx="9" cy="8" r="3.5" />
          <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
          <circle cx="17" cy="9" r="2.5" />
          <path d="M15 20a5 5 0 0 1 6.5-4.8" />
        </Base>
      );
    case "settings":
      return (
        <Base size={size} {...rest}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
        </Base>
      );
    case "shield":
      return (
        <Base size={size} {...rest}>
          <path d="M12 3 4 6v6c0 4.5 3.4 8.4 8 9 4.6-.6 8-4.5 8-9V6l-8-3Z" />
          <path d="m9 12 2 2 4-4" />
        </Base>
      );
    case "logout":
      return (
        <Base size={size} {...rest}>
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <path d="m17 16 4-4-4-4" />
          <path d="M21 12H10" />
        </Base>
      );
    case "search":
      return (
        <Base size={size} {...rest}>
          <circle cx="11" cy="11" r="6.5" />
          <path d="m20 20-4.3-4.3" />
        </Base>
      );
    case "bell":
      return (
        <Base size={size} {...rest}>
          <path d="M6 8a6 6 0 1 1 12 0c0 5 2 6 2 7H4c0-1 2-2 2-7Z" />
          <path d="M10.5 20a1.5 1.5 0 0 0 3 0" />
        </Base>
      );
    case "plus":
      return (
        <Base size={size} {...rest}>
          <path d="M12 5v14M5 12h14" />
        </Base>
      );
    case "menu":
      return (
        <Base size={size} {...rest}>
          <path d="M4 7h16M4 12h16M4 17h16" />
        </Base>
      );
    case "chevron-right":
      return (
        <Base size={size} {...rest}>
          <path d="m9 6 6 6-6 6" />
        </Base>
      );
    case "wifi":
      return (
        <Base size={size} {...rest}>
          <path d="M2 9a16 16 0 0 1 20 0" />
          <path d="M5 12.5a12 12 0 0 1 14 0" />
          <path d="M8 16a8 8 0 0 1 8 0" />
          <circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" />
        </Base>
      );
    case "server":
      return (
        <Base size={size} {...rest}>
          <rect x="3" y="4" width="18" height="7" rx="1.5" />
          <rect x="3" y="13" width="18" height="7" rx="1.5" />
          <path d="M7 7.5h.01M7 16.5h.01" />
        </Base>
      );
    case "clock":
      return (
        <Base size={size} {...rest}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3.5 2" />
        </Base>
      );
    default:
      return <Base size={size} {...rest} />;
  }
}
